const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Properties
export const getProperties = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, value);
    }
  });
  const response = await fetch(`${API}/properties?${params}`, { credentials: 'include' });
  if (!response.ok) throw new Error('Failed to fetch properties');
  return response.json();
};

export const getFeaturedProperties = async () => {
  const response = await fetch(`${API}/properties/featured`, { credentials: 'include' });
  if (!response.ok) throw new Error('Failed to fetch featured properties');
  return response.json();
};

export const getProperty = async (id) => {
  const response = await fetch(`${API}/properties/${id}`, { credentials: 'include' });
  if (!response.ok) throw new Error('Property not found');
  return response.json();
};

export const createProperty = async (data) => {
  const response = await fetch(`${API}/properties`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create property');
  }
  return response.json();
};

export const updateProperty = async (id, data) => {
  const response = await fetch(`${API}/properties/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update property');
  }
  return response.json();
};

export const deleteProperty = async (id) => {
  const response = await fetch(`${API}/properties/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to delete property');
  return response.json();
};

export const getMyProperties = async () => {
  const response = await fetch(`${API}/my-properties`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to fetch properties');
  return response.json();
};

// Saved Properties
export const toggleSaveProperty = async (propertyId) => {
  const response = await fetch(`${API}/saved-properties`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ property_id: propertyId })
  });
  if (!response.ok) throw new Error('Failed to save property');
  return response.json();
};

export const getSavedProperties = async () => {
  const response = await fetch(`${API}/saved-properties`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to fetch saved properties');
  return response.json();
};

export const checkSaved = async (propertyId) => {
  const response = await fetch(`${API}/saved-properties/check/${propertyId}`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) return { saved: false };
  return response.json();
};

// Messages
export const sendMessage = async (data) => {
  const response = await fetch(`${API}/messages`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Failed to send message');
  return response.json();
};

export const getMessages = async () => {
  const response = await fetch(`${API}/messages`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to fetch messages');
  return response.json();
};

export const getConversation = async (userId) => {
  const response = await fetch(`${API}/messages/conversation/${userId}`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to fetch conversation');
  return response.json();
};

export const getUnreadCount = async () => {
  const response = await fetch(`${API}/messages/unread-count`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) return { count: 0 };
  return response.json();
};

// Inquiries
export const createInquiry = async (data) => {
  const response = await fetch(`${API}/inquiries`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Failed to send inquiry');
  return response.json();
};

export const getNewLaunchProjects = async (limit = 8) => {
  const response = await fetch(`${API}/projects/new-launches?limit=${limit}`);
  if (!response.ok) throw new Error('Failed to fetch projects');
  return response.json();
};

export const requestBrochure = async (data) => {
  const response = await fetch(`${API}/leads/brochure`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.detail || 'Failed to request brochure');
  return body;
};


export const getInquiries = async () => {
  const response = await fetch(`${API}/inquiries`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Failed to fetch inquiries');
  return response.json();
};

// EMI Calculator
export const calculateEMI = async (data) => {
  const response = await fetch(`${API}/calculate-emi`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Failed to calculate EMI');
  return response.json();
};

// AI Recommendations
export const getAIRecommendations = async (data) => {
  const response = await fetch(`${API}/ai/recommendations`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Failed to get recommendations');
  return response.json();
};

// Public AI Smart Search (no auth required)
export const aiSmartSearch = async (query) => {
  const response = await fetch(`${API}/ai/smart-search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Smart search failed');
  }
  return response.json();
};

// Search
export const searchProperties = async (query) => {
  const response = await fetch(`${API}/search?q=${encodeURIComponent(query)}`, {
    credentials: 'include'
  });
  if (!response.ok) throw new Error('Search failed');
  return response.json();
};

// Stats
export const getCities = async () => {
  const response = await fetch(`${API}/stats/cities`, { credentials: 'include' });
  if (!response.ok) return { cities: [] };
  return response.json();
};

export const getStats = async () => {
  const response = await fetch(`${API}/stats/overview`, { credentials: 'include' });
  if (!response.ok) return { total_properties: 0, total_users: 0, for_sale: 0, for_rent: 0 };
  return response.json();
};

export const getCitySummary = async (city, locality) => {
  const qs = locality ? `?locality=${encodeURIComponent(locality)}` : '';
  const response = await fetch(`${API}/stats/city/${encodeURIComponent(city)}${qs}`, { credentials: 'include' });
  if (!response.ok) return null;
  return response.json();
};

export const requestAccountDeletion = async (payload) => {
  const response = await fetch(`${API}/account/deletion-request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || 'Failed to submit request');
  return data;
};

// User Profile
export const getUserProfile = async (userId) => {
  const response = await fetch(`${API}/users/${userId}`, { credentials: 'include' });
  if (!response.ok) throw new Error('User not found');
  return response.json();
};

export const getPropertyCount = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.append(key, value);
  });
  const response = await fetch(`${API}/properties/count?${params}`);
  if (!response.ok) throw new Error('Failed to count properties');
  return response.json();
};

export const getSimilarProperties = async (id, limit = 4) => {
  const response = await fetch(`${API}/properties/${id}/similar?limit=${limit}`);
  if (!response.ok) throw new Error('Failed to fetch similar properties');
  return response.json();
};

// Professional Services Directory
export const getProviderCategories = async () => {
  const response = await fetch(`${API}/providers/categories`);
  if (!response.ok) throw new Error('Failed to fetch categories');
  return response.json();
};

export const getProviders = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.append(k, v); });
  const response = await fetch(`${API}/providers?${params}`);
  if (!response.ok) throw new Error('Failed to fetch providers');
  return response.json();
};

export const applyPartner = async (data) => {
  const response = await fetch(`${API}/providers/apply`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
  if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.detail || 'Failed to submit'); }
  return response.json();
};

export const verifyPartnerPayment = async (data) => {
  const response = await fetch(`${API}/providers/verify-payment`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
  if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.detail || 'Payment verification failed'); }
  return response.json();
};

export const getMyProviderListings = async () => {
  const response = await fetch(`${API}/providers/mine`, { headers: getHeaders(), credentials: 'include' });
  if (!response.ok) throw new Error('Failed to load your listings');
  return response.json();
};

export const updateMyProviderListing = async (providerId, data) => {
  const response = await fetch(`${API}/providers/mine/${providerId}`, { method: 'PATCH', headers: getHeaders(), credentials: 'include', body: JSON.stringify(data) });
  if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.detail || 'Failed to update listing'); }
  return response.json();
};

export const adminGetPropertyContacts = async (status = 'all', q = '') => {
  const params = new URLSearchParams({ status, q });
  const response = await fetch(`${API}/admin/properties/contacts?${params}`, { headers: getHeaders(), credentials: 'include' });
  if (!response.ok) throw new Error('Failed to fetch property contacts');
  return response.json();
};

export const adminUpdatePropertyContact = async (propertyId, data) => {
  const response = await fetch(`${API}/admin/properties/${propertyId}/contact`, { method: 'PATCH', headers: getHeaders(), credentials: 'include', body: JSON.stringify(data) });
  if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.detail || 'Failed to update contact'); }
  return response.json();
};

export const adminGetProviders = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.append(k, v); });
  const response = await fetch(`${API}/admin/providers?${params}`, { headers: getHeaders(), credentials: 'include' });
  if (!response.ok) throw new Error('Failed to fetch providers');
  return response.json();
};

export const adminCreateProvider = async (data) => {
  const response = await fetch(`${API}/admin/providers`, { method: 'POST', headers: getHeaders(), credentials: 'include', body: JSON.stringify(data) });
  if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.detail || 'Failed to create'); }
  return response.json();
};

export const adminUpdateProvider = async (id, data) => {
  const response = await fetch(`${API}/admin/providers/${id}`, { method: 'PATCH', headers: getHeaders(), credentials: 'include', body: JSON.stringify(data) });
  if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.detail || 'Failed to update'); }
  return response.json();
};

export const adminDeleteProvider = async (id) => {
  const response = await fetch(`${API}/admin/providers/${id}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' });
  if (!response.ok) throw new Error('Failed to delete');
  return response.json();
};

// Saved Searches
export const createSavedSearch = async (data) => {
  const response = await fetch(`${API}/saved-searches`, { method: 'POST', headers: getHeaders(), credentials: 'include', body: JSON.stringify(data) });
  if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.detail || 'Failed to save search'); }
  return response.json();
};

export const getSavedSearches = async () => {
  const response = await fetch(`${API}/saved-searches`, { headers: getHeaders(), credentials: 'include' });
  if (!response.ok) throw new Error('Failed to load saved searches');
  return response.json();
};

export const updateSavedSearch = async (id, data) => {
  const response = await fetch(`${API}/saved-searches/${id}`, { method: 'PATCH', headers: getHeaders(), credentials: 'include', body: JSON.stringify(data) });
  if (!response.ok) throw new Error('Failed to update');
  return response.json();
};

export const deleteSavedSearch = async (id) => {
  const response = await fetch(`${API}/saved-searches/${id}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' });
  if (!response.ok) throw new Error('Failed to delete');
  return response.json();
};
